import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UsuarioSecundarioService from '../../modules/services/UsuarioSecundarioService';
import '../css/gestaoUsuarios.css';

function CriarUsuarioSecundario({ portalSession, onUsuarioCriado }) {
    const navigate = useNavigate();

    const souMaster = !!portalSession && (portalSession.cdUsuarioMaster === null || portalSession.cdUsuarioMaster === undefined);

    const clientesDisponiveis = useMemo(() => {
        if (!portalSession) {
            return [];
        }
        const vistos = new Set();
        return (portalSession.clientesPerfis || []).filter((item) => {
            if (vistos.has(item.cdCliente)) {
                return false;
            }
            vistos.add(item.cdCliente);
            return true;
        });
    }, [portalSession]);

    const [modulos, setModulos] = useState([]);
    const [loginGerado, setLoginGerado] = useState('');
    const [carregandoLogin, setCarregandoLogin] = useState(true);
    const [dsSenha, setDsSenha] = useState('');
    const [dsEmail, setDsEmail] = useState('');
    const [nmUsuario, setNmUsuario] = useState('');
    const [cdClientesSelecionados, setCdClientesSelecionados] = useState([]);
    // { [cdCliente]: [cdModulo, ...] } — cada CNPJ tem seu próprio conjunto de módulos (RN018).
    const [modulosPorCliente, setModulosPorCliente] = useState({});
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');
    const [filtroCliente, setFiltroCliente] = useState('');
    const [filtroModulo, setFiltroModulo] = useState('');

    useEffect(() => {
        if (!souMaster) {
            return;
        }
        UsuarioSecundarioService.listarModulos()
            .then((lista) => setModulos(lista || []))
            .catch(() => setErro('Não foi possível carregar os módulos disponíveis.'));

        // RN006: o login é gerado pelo sistema — aqui só buscamos o preview para exibir bloqueado.
        UsuarioSecundarioService.buscarProximoLogin(portalSession.usuario.cdUsuario)
            .then((login) => setLoginGerado(login))
            .catch((error) => setErro(error.message || 'Não foi possível gerar o login.'))
            .finally(() => setCarregandoLogin(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [souMaster]);
    const alternarTodosModulosParaTodosClientes = () => {
        const todosModulosSelecionados = clientesSelecionadosInfo.length > 0 &&
            clientesSelecionadosInfo.every((cliente) =>
                modulos.length > 0 &&
                (modulosPorCliente[cliente.cdCliente] || []).length === modulos.length
            );

        if (todosModulosSelecionados) {
            // Desmarcar todos os módulos de todos os clientes
            setModulosPorCliente((atual) => {
                const copia = { ...atual };

                clientesSelecionadosInfo.forEach((cliente) => {
                    copia[cliente.cdCliente] = [];
                });

                return copia;
            });
        } else {
            // Marcar todos os módulos para todos os clientes
            setModulosPorCliente((atual) => {
                const copia = { ...atual };

                clientesSelecionadosInfo.forEach((cliente) => {
                    copia[cliente.cdCliente] = modulos.map(
                        (modulo) => modulo.cdModulo
                    );
                });

                return copia;
            });
        }
    };
    const alternarCliente = (cdCliente) => {
        setCdClientesSelecionados((atual) => {
            const jaSelecionado = atual.includes(cdCliente);
            const novoSelecionados = jaSelecionado ? atual.filter((id) => id !== cdCliente) : [...atual, cdCliente];

            setModulosPorCliente((atualModulos) => {
                const copia = { ...atualModulos };
                if (jaSelecionado) {
                    delete copia[cdCliente];
                } else if (!copia[cdCliente]) {
                    copia[cdCliente] = [];
                }
                return copia;
            });

            return novoSelecionados;
        });
    };

    const alternarSelecionarTodosClientes = () => {
        if (cdClientesSelecionados.length === clientesDisponiveis.length) {
            setCdClientesSelecionados([]);
            setModulosPorCliente({});
        } else {
            setCdClientesSelecionados(clientesDisponiveis.map((item) => item.cdCliente));
            setModulosPorCliente((atual) => {
                const copia = { ...atual };
                clientesDisponiveis.forEach((item) => {
                    if (!copia[item.cdCliente]) {
                        copia[item.cdCliente] = [];
                    }
                });
                return copia;
            });
        }
    };

    const alternarModuloDoCliente = (cdCliente, cdModulo) => {
        setModulosPorCliente((atual) => {
            const modulosDoCliente = atual[cdCliente] || [];
            const novosModulos = modulosDoCliente.includes(cdModulo)
                ? modulosDoCliente.filter((id) => id !== cdModulo)
                : [...modulosDoCliente, cdModulo];
            return { ...atual, [cdCliente]: novosModulos };
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!dsSenha || !nmUsuario.trim()) {
            setErro('Preencha nome do usuário e senha temporária.');
            return;
        }
        if (!dsEmail.trim()) {
            // O e-mail é o canal de recuperação de senha do usuário secundário.
            setErro('O e-mail é obrigatório.');
            return;
        }
        if (cdClientesSelecionados.length === 0) {
            setErro('Selecione ao menos um CNPJ para o usuário secundário.');
            return;
        }
        const clienteSemModulo = cdClientesSelecionados.find((cdCliente) => (modulosPorCliente[cdCliente] || []).length === 0);
        if (clienteSemModulo) {
            setErro('Selecione ao menos um módulo para cada CNPJ vinculado.');
            return;
        }

        setErro('');
        setEnviando(true);
        try {
            const atribuicoes = cdClientesSelecionados.map((cdCliente) => ({
                cdCliente,
                cdModulos: modulosPorCliente[cdCliente] || [],
            }));

            await UsuarioSecundarioService.criar({
                cdUsuarioMaster: portalSession.usuario.cdUsuario,
                dsSenha,
                dsEmail: dsEmail.trim(),
                nmUsuario: nmUsuario.trim(),
                atribuicoes,
            });
            if (onUsuarioCriado) {
                // Recarrega a lista completa e atualizada na sessão antes de navegar — assim
                // GestaoUsuarios já abre com a lista certa, sem depender da "foto" do login.
                await onUsuarioCriado();
            }
            setSucesso('Usuário criado com sucesso.');
            setTimeout(() => navigate('/gestao-usuarios'), 1200);
        } catch (error) {
            setErro(error.message || 'Erro ao criar usuário secundário.');
        } finally {
            setEnviando(false);
        }
    };

    if (!souMaster) {
        return (
            <div className="gestao-usuarios-page">
                <p className="gestao-usuarios-restrito">Esta área é exclusiva para o usuário master.</p>
            </div>
        );
    }

    const clientesSelecionadosInfo = clientesDisponiveis.filter((item) => cdClientesSelecionados.includes(item.cdCliente));

    const clientesFiltrados = clientesDisponiveis.filter((cliente) => {
        const busca = filtroCliente.trim().toLowerCase();

        if (!busca) {
            return true;
        }

        return (
            String(cliente.nmCliente || '').toLowerCase().includes(busca) ||
            String(cliente.dsCnpjCpf || '').toLowerCase().includes(busca)
        );
    });

    const modulosFiltrados = modulos.filter((modulo) => {
        const busca = filtroModulo.trim().toLowerCase();

        if (!busca) {
            return true;
        }

        return String(modulo.nmModulo || '').toLowerCase().includes(busca);
    });

    return (
        <div className="dashboard">
            <div className="dashboard-content">
                <div className="dashboard-painel gestao-usuarios">
                    <h3 className='titulo'>Criar usuário secundário</h3>

                    <form className="criar-usuario-form" onSubmit={handleSubmit}>
                        <section className="criar-usuario-secao">
                            <h2>1. Credenciais de acesso</h2>

                            <div className="criar-usuario-grid">
                                <div className="criar-usuario-campo">
                                    <label htmlFor="csec-login">Login (gerado automaticamente)</label>
                                    <input
                                        id="csec-login"
                                        type="text"
                                        value={carregandoLogin ? 'Gerando...' : loginGerado}
                                        readOnly
                                        disabled
                                        className="criar-usuario-login-gerado"
                                    />
                                </div>
                                <div className="criar-usuario-campo">
                                    <label htmlFor="csec-nome">Nome do usuário</label>
                                    <input
                                        id="csec-nome"
                                        type="text"
                                        value={nmUsuario}
                                        onChange={(e) => setNmUsuario(e.target.value)}
                                        disabled={enviando}
                                    />
                                </div>



                                <div className="criar-usuario-campo">
                                    <label htmlFor="csec-senha">Senha temporária</label>
                                    <input
                                        id="csec-senha"
                                        type="password"
                                        value={dsSenha}
                                        onChange={(e) => setDsSenha(e.target.value)}
                                        disabled={enviando}
                                    />
                                </div>

                                <div className="criar-usuario-campo">
                                    <label htmlFor="csec-email">E-mail</label>
                                    <input
                                        id="csec-email"
                                        type="email"
                                        value={dsEmail}
                                        onChange={(e) => setDsEmail(e.target.value)}
                                        disabled={enviando}
                                        required
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="criar-usuario-secao">
                            <h2>2. Vincular unidades (CNPJs)</h2>
<div className="criar-usuario-filtro">
    <input
        type="text"
        placeholder="Buscar cliente ou CNPJ..."
        value={filtroCliente}
        onChange={(e) => setFiltroCliente(e.target.value)}
        disabled={enviando}
    />
</div>
                            <div className="criar-usuario-select-all">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={
                                            clientesDisponiveis.length > 0 &&
                                            cdClientesSelecionados.length === clientesDisponiveis.length
                                        }
                                        onChange={alternarSelecionarTodosClientes}
                                        disabled={enviando}
                                    />
                                    Selecionar todos os CNPJs
                                </label>

                                <span>
                                    {cdClientesSelecionados.length} de {clientesDisponiveis.length} selecionados
                                </span>
                            </div>

                            {/* Área limitada com rolagem */}
                            <div className="criar-usuario-clientes-container">
                                <div className="criar-usuario-grid">
                                    {clientesFiltrados.map((item) => (
                                        <label
                                            key={item.cdCliente}
                                            className={`criar-usuario-item ${cdClientesSelecionados.includes(item.cdCliente)
                                                ? 'is-checked'
                                                : ''
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={cdClientesSelecionados.includes(item.cdCliente)}
                                                onChange={() => alternarCliente(item.cdCliente)}
                                                disabled={enviando}
                                            />

                                            <span>
                                                <strong>{item.nmCliente}</strong>
                                                <small>{item.dsCnpjCpf}</small>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </section>

                        <section className="criar-usuario-secao">
                            <h2>3. Módulos / permissões por CNPJ</h2>

                            <p className="criar-usuario-hint">
                                Cada unidade pode ter um conjunto diferente de módulos liberados.
                            </p>
<div className="criar-usuario-filtro">
    <input
        type="text"
        placeholder="Buscar módulo..."
        value={filtroModulo}
        onChange={(e) => setFiltroModulo(e.target.value)}
        disabled={enviando}
    />
</div>
                            {clientesSelecionadosInfo.length === 0 ? (
                                <p className="criar-usuario-hint">
                                    Selecione ao menos um CNPJ na seção anterior.
                                </p>
                            ) : (
                                <>
                                    <div className="criar-usuario-select-all">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={
                                                    clientesSelecionadosInfo.length > 0 &&
                                                    modulos.length > 0 &&
                                                    clientesSelecionadosInfo.every(
                                                        (cliente) =>
                                                            (modulosPorCliente[cliente.cdCliente] || []).length ===
                                                            modulos.length
                                                    )
                                                }
                                                onChange={alternarTodosModulosParaTodosClientes}
                                                disabled={enviando || modulos.length === 0}
                                            />
                                            Selecionar todos os módulos para todos os CNPJs
                                        </label>

                                        <span>
                                            {modulos.length} módulos disponíveis
                                        </span>
                                    </div>

                                    <div className="criar-usuario-permissoes-container">
                                        {clientesSelecionadosInfo.map((item) => (
                                            <div
                                                key={item.cdCliente}
                                                className="criar-usuario-modulos-cliente"
                                            >
                                                <h3>
                                                    {item.nmCliente}{' '}
                                                    <small>{item.dsCnpjCpf}</small>
                                                </h3>

                                                <div className="criar-usuario-grid">
                                                    {modulosFiltrados.map((modulo) => {
                                                        const selecionado =
                                                            (modulosPorCliente[item.cdCliente] || [])
                                                                .includes(modulo.cdModulo);

                                                        return (
                                                            <label
                                                                key={modulo.cdModulo}
                                                                className={`criar-usuario-item ${selecionado ? 'is-checked' : ''
                                                                    }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selecionado}
                                                                    onChange={() =>
                                                                        alternarModuloDoCliente(
                                                                            item.cdCliente,
                                                                            modulo.cdModulo
                                                                        )
                                                                    }
                                                                    disabled={enviando}
                                                                />

                                                                <span>
                                                                    <strong>{modulo.nmModulo}</strong>
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </section>
                        {erro && <div className="gestao-usuarios-erro">{erro}</div>}
                        {sucesso && <div className="gestao-usuarios-sucesso">{sucesso}</div>}

                        <div className="criar-usuario-acoes">
                            <button type="submit" className="gestao-usuarios-btn-novo" disabled={enviando}>
                                {enviando ? 'Criando...' : 'Criar usuário'}
                            </button>
                            <button type="button" className="gestao-usuarios-btn-toggle" onClick={() => navigate('/gestao-usuarios')} disabled={enviando}>
                                Cancelar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default CriarUsuarioSecundario;
